import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGasI18n, useGasNotify } from '../../shared/kit';
import { formatPrice } from '../shop-shared/format';
import { errorExtras } from '../shop-shared/transport';
import { getCheckoutData, processCheckout, quoteCheckout } from './api';
import type {
    CheckoutAddress,
    CheckoutShippingMethodOption,
    ShopCheckoutInitData,
} from './types';

interface AppProps {
    initData: ShopCheckoutInitData;
}

const REQUIRED_BILLING = [
    'billing_first_name',
    'billing_last_name',
    'billing_email',
    'billing_address_1',
    'billing_postcode',
    'billing_city',
] as const;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const QUOTE_DEBOUNCE_MS = 350;

const EMPTY_ADDRESS: CheckoutAddress = {
    first_name: '', last_name: '', company: '', email: '', phone: '',
    address_1: '', postcode: '', city: '', country: 'CH',
};

export default function App({ initData }: AppProps) {
    const { t } = useGasI18n();
    const notify = useGasNotify();

    const [cfg, setCfg] = useState<ShopCheckoutInitData>(initData);
    const [billing, setBilling] = useState<CheckoutAddress>({ ...EMPTY_ADDRESS, ...(initData.billing || {}) });
    const [shipping, setShipping] = useState<CheckoutAddress>({ ...EMPTY_ADDRESS, ...(initData.shipping || {}) });
    const [orderNotes, setOrderNotes] = useState('');
    const [paymentMethod, setPaymentMethod] = useState<string>(initData.payment_methods?.default || 'card');
    const [shippingMethod, setShippingMethod] = useState<string>(initData.shipping_quote?.method || 'delivery');
    const [shippingMethods, setShippingMethods] = useState<CheckoutShippingMethodOption[]>(
        Array.isArray(initData.shipping_methods) ? initData.shipping_methods : [],
    );
    const [quoting, setQuoting] = useState(false);
    const [acceptTerms, setAcceptTerms] = useState<boolean>(Boolean(initData.user_preferences?.auto_accept_terms));
    const [acceptPrivacy, setAcceptPrivacy] = useState<boolean>(Boolean(initData.user_preferences?.auto_accept_privacy));
    const [subscribeNewsletter, setSubscribeNewsletter] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [submitting, setSubmitting] = useState(false);
    const [shippingCopied, setShippingCopied] = useState(false);

    const quoteSeqRef = useRef(0);
    const quoteAbortRef = useRef<AbortController | null>(null);
    const skipInitialQuoteRef = useRef(true);

    const isFreeOrder = useMemo(() => Number(cfg.total || 0) === 0, [cfg.total]);
    const shippingRequired = Boolean(cfg.shipping_quote?.required);
    const canSubmit = acceptTerms && acceptPrivacy && !submitting && !quoting;
    const taxQuote = cfg.tax_quote;
    const showTax = Boolean(taxQuote?.enabled) && Number(taxQuote?.tax_total || 0) > 0;
    const taxLabel = taxQuote?.prices_include_tax
        ? t('shop.tax.vat_included_rate', { rate: taxQuote.rate })
        : t('shop.tax.vat_rate', { rate: taxQuote?.rate || 0 });

    const submitButtonText = useMemo(() => {
        if (isFreeOrder) return t('shop.checkout.submit_confirm_registration');
        if (paymentMethod === 'card') return t('shop.checkout.submit_pay_by_card', { amount: formatPrice(cfg.total) });
        if (paymentMethod === 'bulletin') return t('shop.checkout.submit_order', { amount: formatPrice(cfg.total) });
        return t('shop.checkout.submit_confirm_order');
    }, [isFreeOrder, paymentMethod, cfg.total, t]);

    // Hydratation si le bootstrap serveur n'a pas fourni les items/pays.
    useEffect(() => {
        const needsHydration = !Array.isArray(cfg.items) || cfg.items.length === 0
            || !Array.isArray(cfg.countries) || cfg.countries.length === 0;
        if (!needsHydration) return;
        getCheckoutData(initData).then((data) => {
            // Deep-merge des urls : ne pas perdre thankyou/checkout si une version
            // d'hydratation renvoyait un sous-ensemble.
            setCfg((prev) => ({ ...prev, ...data, urls: { ...prev.urls, ...(data.urls || {}) } }));
            if (data.billing) setBilling((p) => ({ ...p, ...data.billing }));
            if (data.shipping) setShipping((p) => ({ ...p, ...data.shipping }));
            if (data.payment_methods?.default) setPaymentMethod(data.payment_methods.default);
            if (Array.isArray(data.shipping_methods)) setShippingMethods(data.shipping_methods);
            if (data.shipping_quote?.method) setShippingMethod(data.shipping_quote.method);
        }).catch(() => { /* tolère un échec */ });
    }, [cfg.items, cfg.countries, initData]);

    const applyQuoteResult = useCallback((data: Awaited<ReturnType<typeof quoteCheckout>>, sequence: number) => {
        // Réponse périmée : une requête plus récente a déjà été émise.
        if (sequence !== quoteSeqRef.current) {
            return;
        }

        setCfg((prev) => ({
            ...prev,
            shipping_quote: data.shipping_quote ?? prev.shipping_quote,
            shipping_methods: data.shipping_methods ?? prev.shipping_methods,
            tax_quote: data.tax_quote ?? prev.tax_quote,
            subtotal: typeof data.subtotal === 'number' ? data.subtotal : prev.subtotal,
            discount_total: typeof data.discount_total === 'number' ? data.discount_total : prev.discount_total,
            total: typeof data.total === 'number' ? data.total : prev.total,
            payment_methods: data.payment_methods
                ? { ...prev.payment_methods, ...data.payment_methods }
                : prev.payment_methods,
            countries: Array.isArray(data.countries) && data.countries.length > 0
                ? data.countries
                : prev.countries,
        }));

        if (Array.isArray(data.shipping_methods)) {
            setShippingMethods(data.shipping_methods);
            const stillAvailable = data.shipping_methods.some(
                (m) => m.method === shippingMethod && m.available,
            );
            if (!stillAvailable) {
                const firstOk = data.shipping_methods.find((m) => m.available);
                if (firstOk) {
                    setShippingMethod(firstOk.method);
                }
            }
        }

        if (data.payment_methods?.default) {
            const nextDefault = data.payment_methods.default;
            if (nextDefault === 'free' || data.payment_methods.free) {
                setPaymentMethod('free');
            } else if (nextDefault === 'card' && data.payment_methods.show_card) {
                setPaymentMethod((prev) => (prev === 'free' ? 'card' : prev));
            } else if (nextDefault === 'bulletin' && data.payment_methods.show_bulletin) {
                setPaymentMethod((prev) => (prev === 'free' ? 'bulletin' : prev));
            }
        }

        const quoteErrors = data.shipping_quote?.errors;
        if (Array.isArray(quoteErrors) && quoteErrors.length > 0) {
            setErrors((prev) => ({
                ...prev,
                shipping_country: quoteErrors[0],
            }));
        } else {
            setErrors((prev) => {
                if (!prev.shipping_country) return prev;
                const next = { ...prev };
                delete next.shipping_country;
                return next;
            });
        }
    }, [shippingMethod]);

    // Re-quote debounce + numéro de séquence (F10).
    useEffect(() => {
        if (!shippingRequired) {
            return;
        }
        // Skip le tout premier rendu : le serveur a déjà fourni un quote initial.
        if (skipInitialQuoteRef.current) {
            skipInitialQuoteRef.current = false;
            return;
        }

        const country = (shipping.country || billing.country || 'CH').toUpperCase();
        const timer = window.setTimeout(() => {
            const sequence = quoteSeqRef.current + 1;
            quoteSeqRef.current = sequence;
            quoteAbortRef.current?.abort();
            const controller = new AbortController();
            quoteAbortRef.current = controller;
            setQuoting(true);

            quoteCheckout(cfg, {
                country,
                shipping_country: country,
                billing_country: (billing.country || country).toUpperCase(),
                method: shippingMethod,
                sequence,
            }, controller.signal)
                .then((data) => applyQuoteResult(data, sequence))
                .catch((error) => {
                    if ((error as Error)?.name === 'AbortError') return;
                    // Tolère un échec réseau : le serveur re-quote à la soumission.
                })
                .finally(() => {
                    if (sequence === quoteSeqRef.current) {
                        setQuoting(false);
                    }
                });
        }, QUOTE_DEBOUNCE_MS);

        return () => {
            window.clearTimeout(timer);
        };
        // cfg volontairement hors deps : on ne re-quote pas sur chaque patch de total.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shipping.country, billing.country, shippingMethod, shippingRequired, applyQuoteResult]);

    useEffect(() => () => {
        quoteAbortRef.current?.abort();
    }, []);

    const setBillingField = useCallback((key: keyof CheckoutAddress, value: string) => {
        setBilling((prev) => ({ ...prev, [key]: value }));
    }, []);
    const setShippingField = useCallback((key: keyof CheckoutAddress, value: string) => {
        setShipping((prev) => ({ ...prev, [key]: value }));
    }, []);

    const validateField = useCallback((fieldName: string): boolean => {
        const key = fieldName.replace('billing_', '') as keyof CheckoutAddress;
        const value = String(billing[key] || '').trim();
        if ((REQUIRED_BILLING as readonly string[]).includes(fieldName) && value === '') {
            setErrors((prev) => ({ ...prev, [fieldName]: t('shop.checkout.field_required') }));
            return false;
        }
        if (fieldName === 'billing_email' && value !== '' && !EMAIL_RE.test(value)) {
            setErrors((prev) => ({ ...prev, [fieldName]: t('errors.invalid_email') }));
            return false;
        }
        setErrors((prev) => {
            const next = { ...prev };
            delete next[fieldName];
            return next;
        });
        return true;
    }, [billing, t]);

    const validateAll = useCallback((): boolean => {
        const nextErrors: Record<string, string> = {};
        REQUIRED_BILLING.forEach((fieldName) => {
            const key = fieldName.replace('billing_', '') as keyof CheckoutAddress;
            const value = String(billing[key] || '').trim();
            if (value === '') {
                nextErrors[fieldName] = t('shop.checkout.field_required');
            } else if (fieldName === 'billing_email' && !EMAIL_RE.test(value)) {
                nextErrors[fieldName] = t('errors.invalid_email');
            }
        });
        if (!acceptTerms) nextErrors.accept_terms = t('shop.checkout.field_required');
        if (!acceptPrivacy) nextErrors.accept_privacy = t('shop.checkout.field_required');
        if (shippingRequired && shippingMethod === 'delivery' && cfg.shipping_quote && !cfg.shipping_quote.available) {
            nextErrors.shipping_country = (cfg.shipping_quote.errors && cfg.shipping_quote.errors[0])
                || t('shop.shipping.country_not_allowed');
        }
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    }, [billing, acceptTerms, acceptPrivacy, shippingRequired, shippingMethod, cfg.shipping_quote, t]);

    const copyBillingToShipping = useCallback(() => {
        setShipping({
            first_name: billing.first_name, last_name: billing.last_name, company: billing.company,
            address_1: billing.address_1, postcode: billing.postcode, city: billing.city, country: billing.country,
        });
        setShippingCopied(true);
        window.setTimeout(() => setShippingCopied(false), 2000);
    }, [billing]);

    const thankYouUrl = useCallback((orderId: number): string => {
        if (cfg.urls?.thankyou) {
            return `${cfg.urls.thankyou}?order_id=${orderId}`;
        }
        return (cfg.urls?.cart || '/shop/panier/').replace('/panier/', `/merci/?order_id=${orderId}`);
    }, [cfg.urls]);

    const submitCheckout = useCallback(async () => {
        if (submitting) return;
        if (!validateAll()) {
            notify.error(t('shop.checkout.form_errors'));
            return;
        }
        setSubmitting(true);

        const method = isFreeOrder ? 'free' : paymentMethod;
        const payload: Record<string, unknown> = {
            gas_checkout_nonce: cfg.nonce || initData.nonce,
            billing_first_name: billing.first_name,
            billing_last_name: billing.last_name,
            billing_company: billing.company,
            billing_email: billing.email,
            billing_phone: billing.phone,
            billing_address_1: billing.address_1,
            billing_postcode: billing.postcode,
            billing_city: billing.city,
            billing_country: billing.country,
            shipping_first_name: shipping.first_name,
            shipping_last_name: shipping.last_name,
            shipping_company: shipping.company,
            shipping_address_1: shipping.address_1,
            shipping_postcode: shipping.postcode,
            shipping_city: shipping.city,
            shipping_country: shipping.country,
            shipping_method: shippingMethod,
            order_notes: orderNotes,
            payment_method: method,
        };
        if (acceptTerms) payload.accept_terms = '1';
        if (acceptPrivacy) payload.accept_privacy = '1';
        if (subscribeNewsletter) payload.subscribe_newsletter = '1';

        try {
            const data = await processCheckout(cfg, payload);
            notify.success(t('shop.checkout.success_redirecting'));

            if (data.redirect_url) {
                window.location.href = data.redirect_url;
                return;
            }
            if (data.order_id) {
                window.location.href = thankYouUrl(data.order_id);
                return;
            }
            // Aucune cible de redirection : on relâche le verrou.
            setSubmitting(false);
        } catch (error) {
            setSubmitting(false);
            const extras = errorExtras(error);
            if (extras.errors && typeof extras.errors === 'object') {
                setErrors((prev) => ({ ...prev, ...(extras.errors as Record<string, string>) }));
            }
            notify.error((error as Error).message || t('shop.checkout.process_error'));
        }
    }, [submitting, validateAll, isFreeOrder, paymentMethod, cfg, initData.nonce, billing, shipping, shippingMethod, orderNotes, acceptTerms, acceptPrivacy, subscribeNewsletter, notify, t, thankYouUrl]);

    const countries = Array.isArray(cfg.countries) ? cfg.countries : [];
    const items = Array.isArray(cfg.items) ? cfg.items : [];
    const methodOptions = shippingMethods.length > 0
        ? shippingMethods
        : (shippingRequired
            ? [
                {
                    method: 'delivery',
                    label: cfg.shipping_quote?.label || t('shop.checkout.shipping_method.delivery'),
                    amount: Number(cfg.shipping_quote?.amount || 0),
                    available: Boolean(cfg.shipping_quote?.available),
                    free_shipping: Boolean(cfg.shipping_quote?.free_shipping),
                    errors: cfg.shipping_quote?.errors,
                },
            ]
            : []);

    const formatMethodAmount = (option: CheckoutShippingMethodOption): string => {
        if (!option.available) {
            return t('shop.checkout.shipping_method.unavailable');
        }
        if (option.free_shipping || Number(option.amount || 0) <= 0) {
            return t('shop.checkout.shipping_method.amount_free');
        }
        return formatPrice(option.amount);
    };

    return (
        <div>
            {cfg.payment_return && cfg.payment_return.has_notification && (
                <div className={`gas-shop-payment-notification gas-notification-${cfg.payment_return.type}`}>
                    <div className="gas-notification-content">
                        <span className="gas-notification-icon">{cfg.payment_return.icon || ''}</span>
                        <div>
                            <strong>{cfg.payment_return.title || ''}</strong>
                            <p>{cfg.payment_return.message || ''}</p>
                        </div>
                    </div>
                </div>
            )}

            <form onSubmit={(e) => { e.preventDefault(); submitCheckout(); }} className="gas-checkout-layout">
                <div className="gas-checkout-forms">
                    {/* BILLING */}
                    <div className="gas-checkout-section">
                        <h2 className="gas-checkout-section__title">{t('shop.checkout.billing_information')}</h2>
                        <div className="gas-checkout-fields-grid">
                            <div className={`gas-form-field${errors.billing_first_name ? ' gas-field-error' : ''}`}>
                                <label htmlFor="billing_first_name">{t('shop.checkout.fields.first_name_required')}</label>
                                <input type="text" id="billing_first_name" value={billing.first_name || ''} autoComplete="given-name"
                                    onChange={(e) => setBillingField('first_name', e.target.value)} onBlur={() => validateField('billing_first_name')} />
                                {errors.billing_first_name && <span className="gas-field-error-msg">{errors.billing_first_name}</span>}
                            </div>
                            <div className={`gas-form-field${errors.billing_last_name ? ' gas-field-error' : ''}`}>
                                <label htmlFor="billing_last_name">{t('shop.checkout.fields.last_name_required')}</label>
                                <input type="text" id="billing_last_name" value={billing.last_name || ''} autoComplete="family-name"
                                    onChange={(e) => setBillingField('last_name', e.target.value)} onBlur={() => validateField('billing_last_name')} />
                                {errors.billing_last_name && <span className="gas-field-error-msg">{errors.billing_last_name}</span>}
                            </div>
                            <div className="gas-form-field gas-form-field--full">
                                <label htmlFor="billing_company">{t('shop.checkout.fields.company')}</label>
                                <input type="text" id="billing_company" value={billing.company || ''} autoComplete="organization"
                                    onChange={(e) => setBillingField('company', e.target.value)} />
                            </div>
                            <div className={`gas-form-field${errors.billing_email ? ' gas-field-error' : ''}`}>
                                <label htmlFor="billing_email">{t('shop.checkout.fields.email_required')}</label>
                                <input type="email" id="billing_email" value={billing.email || ''} autoComplete="email"
                                    onChange={(e) => setBillingField('email', e.target.value)} onBlur={() => validateField('billing_email')} />
                                {errors.billing_email && <span className="gas-field-error-msg">{errors.billing_email}</span>}
                            </div>
                            <div className="gas-form-field">
                                <label htmlFor="billing_phone">{t('shop.checkout.fields.phone')}</label>
                                <input type="tel" id="billing_phone" value={billing.phone || ''} autoComplete="tel"
                                    onChange={(e) => setBillingField('phone', e.target.value)} />
                            </div>
                            <div className={`gas-form-field gas-form-field--full${errors.billing_address_1 ? ' gas-field-error' : ''}`}>
                                <label htmlFor="billing_address_1">{t('shop.checkout.fields.address_required')}</label>
                                <input type="text" id="billing_address_1" value={billing.address_1 || ''} autoComplete="address-line1"
                                    onChange={(e) => setBillingField('address_1', e.target.value)} onBlur={() => validateField('billing_address_1')} />
                                {errors.billing_address_1 && <span className="gas-field-error-msg">{errors.billing_address_1}</span>}
                            </div>
                            <div className={`gas-form-field${errors.billing_postcode ? ' gas-field-error' : ''}`}>
                                <label htmlFor="billing_postcode">{t('shop.checkout.fields.postcode_required')}</label>
                                <input type="text" id="billing_postcode" value={billing.postcode || ''} autoComplete="postal-code"
                                    onChange={(e) => setBillingField('postcode', e.target.value)} onBlur={() => validateField('billing_postcode')} />
                                {errors.billing_postcode && <span className="gas-field-error-msg">{errors.billing_postcode}</span>}
                            </div>
                            <div className={`gas-form-field${errors.billing_city ? ' gas-field-error' : ''}`}>
                                <label htmlFor="billing_city">{t('shop.checkout.fields.city_required')}</label>
                                <input type="text" id="billing_city" value={billing.city || ''} autoComplete="address-level2"
                                    onChange={(e) => setBillingField('city', e.target.value)} onBlur={() => validateField('billing_city')} />
                                {errors.billing_city && <span className="gas-field-error-msg">{errors.billing_city}</span>}
                            </div>
                            <div className="gas-form-field">
                                <label htmlFor="billing_country">{t('shop.checkout.fields.country')}</label>
                                <select id="billing_country" value={billing.country || 'CH'} autoComplete="country"
                                    onChange={(e) => setBillingField('country', e.target.value)}>
                                    {countries.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* SHIPPING ADDRESS */}
                    {shippingRequired && (
                        <div className="gas-checkout-section">
                            <div className="gas-checkout-section__header">
                                <h2 className="gas-checkout-section__title">{t('shop.checkout.shipping_address')}</h2>
                                <button type="button" className="gas-button gas-button--outline gas-button--sm" onClick={copyBillingToShipping}>
                                    {shippingCopied ? t('common.copied') : t('shop.checkout.copy_from_billing')}
                                </button>
                            </div>
                            <div className="gas-checkout-fields-grid">
                                <div className="gas-form-field">
                                    <label htmlFor="shipping_first_name">{t('shop.checkout.fields.first_name')}</label>
                                    <input type="text" id="shipping_first_name" value={shipping.first_name || ''} onChange={(e) => setShippingField('first_name', e.target.value)} />
                                </div>
                                <div className="gas-form-field">
                                    <label htmlFor="shipping_last_name">{t('shop.checkout.fields.last_name')}</label>
                                    <input type="text" id="shipping_last_name" value={shipping.last_name || ''} onChange={(e) => setShippingField('last_name', e.target.value)} />
                                </div>
                                <div className="gas-form-field gas-form-field--full">
                                    <label htmlFor="shipping_company">{t('shop.checkout.fields.company')}</label>
                                    <input type="text" id="shipping_company" value={shipping.company || ''} onChange={(e) => setShippingField('company', e.target.value)} />
                                </div>
                                <div className="gas-form-field gas-form-field--full">
                                    <label htmlFor="shipping_address_1">{t('shop.checkout.fields.address')}</label>
                                    <input type="text" id="shipping_address_1" value={shipping.address_1 || ''} onChange={(e) => setShippingField('address_1', e.target.value)} />
                                </div>
                                <div className="gas-form-field">
                                    <label htmlFor="shipping_postcode">{t('shop.checkout.fields.postcode')}</label>
                                    <input type="text" id="shipping_postcode" value={shipping.postcode || ''} onChange={(e) => setShippingField('postcode', e.target.value)} />
                                </div>
                                <div className="gas-form-field">
                                    <label htmlFor="shipping_city">{t('shop.checkout.fields.city')}</label>
                                    <input type="text" id="shipping_city" value={shipping.city || ''} onChange={(e) => setShippingField('city', e.target.value)} />
                                </div>
                                <div className={`gas-form-field${errors.shipping_country ? ' gas-field-error' : ''}`}>
                                    <label htmlFor="shipping_country">{t('shop.checkout.fields.country')}</label>
                                    <select
                                        id="shipping_country"
                                        value={shipping.country || 'CH'}
                                        data-testid="gas-checkout-shipping-country"
                                        onChange={(e) => setShippingField('country', e.target.value)}
                                    >
                                        {countries.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                                    </select>
                                    {errors.shipping_country && <span className="gas-field-error-msg">{errors.shipping_country}</span>}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* SHIPPING METHOD */}
                    {shippingRequired && methodOptions.length > 0 && (
                        <div className="gas-checkout-section" data-testid="gas-checkout-shipping-methods">
                            <h2 className="gas-checkout-section__title">{t('shop.checkout.shipping_method.title')}</h2>
                            {quoting && (
                                <p className="gas-checkout-quote-status" aria-live="polite">
                                    {t('shop.checkout.shipping_method.recalculating')}
                                </p>
                            )}
                            <div className="gas-shipping-methods" role="radiogroup" aria-label={t('shop.checkout.shipping_method.title')}>
                                {methodOptions.map((option) => {
                                    const disabled = !option.available && option.method === 'delivery';
                                    return (
                                        <label
                                            key={option.method}
                                            className={`gas-shipping-method${shippingMethod === option.method ? ' gas-shipping-method--active' : ''}${disabled ? ' gas-shipping-method--disabled' : ''}`}
                                        >
                                            <input
                                                type="radio"
                                                name="shipping_method"
                                                value={option.method}
                                                checked={shippingMethod === option.method}
                                                disabled={disabled && option.method !== 'pickup'}
                                                data-testid={`gas-checkout-shipping-method-${option.method}`}
                                                onChange={() => setShippingMethod(option.method)}
                                            />
                                            <span className="gas-shipping-method__label">{option.label}</span>
                                            <span className="gas-shipping-method__amount">{formatMethodAmount(option)}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* NOTES */}
                    <div className="gas-checkout-section">
                        <h2 className="gas-checkout-section__title">{t('shop.checkout.order_notes')}</h2>
                        <div className="gas-form-field">
                            <textarea id="order_notes" rows={3} value={orderNotes} placeholder={t('shop.checkout.order_notes_placeholder')}
                                onChange={(e) => setOrderNotes(e.target.value)} />
                        </div>
                    </div>
                </div>

                <div className="gas-checkout-sidebar">
                    {/* ORDER REVIEW */}
                    <div className="gas-checkout-section">
                        <h2 className="gas-checkout-section__title">{t('shop.checkout.summary')}</h2>
                        <div className="gas-order-items">
                            {items.map((item, idx) => (
                                <div key={idx} className="gas-order-item">
                                    <div className="gas-order-item__info">
                                        <a href={item.product_url} className="gas-order-item__title">{item.product_title}</a>
                                        {item.variante && <span className="gas-order-item__variant">{item.variante.icon} {item.variante.nom}</span>}
                                        <span className="gas-order-item__qty">× {item.quantity}</span>
                                    </div>
                                    <span className="gas-order-item__price">{formatPrice(item.prix_total)}</span>
                                </div>
                            ))}
                        </div>
                        <div className="gas-order-totals">
                            <div className="gas-order-totals__row">
                                <span>{t('shop.checkout.subtotal')}</span>
                                <strong>{formatPrice(cfg.subtotal)}</strong>
                            </div>
                            {cfg.discount_total > 0 && (
                                <div className="gas-order-totals__row gas-order-totals__row--discount">
                                    <span>{t('shop.checkout.coupon_discount')}</span>
                                    <strong>-{formatPrice(cfg.discount_total)}</strong>
                                </div>
                            )}
                            {cfg.shipping_quote?.required && (
                                <div className="gas-order-totals__row" data-testid="gas-checkout-shipping-total">
                                    <span>{cfg.shipping_quote.label || t('shop.shipping.delivery')}</span>
                                    <strong>
                                        {Number(cfg.shipping_quote.amount || 0) > 0
                                            ? formatPrice(cfg.shipping_quote.amount)
                                            : t('shop.shipping.free')}
                                    </strong>
                                </div>
                            )}
                            {showTax && (
                                <div className="gas-order-totals__row">
                                    <span>{taxLabel}</span>
                                    <strong>{formatPrice(taxQuote?.tax_total || 0)}</strong>
                                </div>
                            )}
                            <div className="gas-order-totals__row gas-order-totals__row--total">
                                <span>{t('shop.checkout.total')}</span>
                                <strong data-testid="gas-checkout-total">{formatPrice(cfg.total)}</strong>
                            </div>
                        </div>
                    </div>

                    {/* PAYMENT METHOD */}
                    <div className="gas-checkout-section">
                        <h2 className="gas-checkout-section__title">{t('shop.checkout.payment_method')}</h2>
                        {isFreeOrder ? (
                            <div className="gas-payment-free-notice">
                                <p>✅ {t('shop.checkout.free_order_notice')}</p>
                            </div>
                        ) : (
                            <div className="gas-payment-methods">
                                {cfg.payment_methods?.show_card && (
                                    <label className={`gas-payment-method${paymentMethod === 'card' ? ' gas-payment-method--active' : ''}`}>
                                        <input type="radio" name="payment_method" value="card" checked={paymentMethod === 'card'} onChange={() => setPaymentMethod('card')} />
                                        <span className="gas-payment-method__label">💳 {t('shop.checkout.payment_card')}</span>
                                        {paymentMethod === 'card' && <div className="gas-payment-method__desc">{t('shop.checkout.payment_card_description')}</div>}
                                    </label>
                                )}
                                {cfg.payment_methods?.show_bulletin && (
                                    <label className={`gas-payment-method${paymentMethod === 'bulletin' ? ' gas-payment-method--active' : ''}`}>
                                        <input type="radio" name="payment_method" value="bulletin" checked={paymentMethod === 'bulletin'} onChange={() => setPaymentMethod('bulletin')} />
                                        <span className="gas-payment-method__label">🏦 {t('shop.checkout.payment_bulletin')}</span>
                                        {paymentMethod === 'bulletin' && <div className="gas-payment-method__desc">{t('shop.checkout.payment_bulletin_description')}</div>}
                                    </label>
                                )}
                            </div>
                        )}
                    </div>

                    {/* TERMS & PRIVACY */}
                    <div className="gas-checkout-section gas-checkout-section--terms">
                        <label className={`gas-checkbox-custom${errors.accept_terms ? ' gas-field-error' : ''}`}>
                            <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} />
                            <span>{t('shop.checkout.accept_terms_prefix')} <a href={cfg.urls?.terms} target="_blank" rel="noreferrer">{t('shop.checkout.accept_terms_link')}</a> *</span>
                        </label>
                        <label className={`gas-checkbox-custom${errors.accept_privacy ? ' gas-field-error' : ''}`}>
                            <input type="checkbox" checked={acceptPrivacy} onChange={(e) => setAcceptPrivacy(e.target.checked)} />
                            <span>{t('shop.checkout.accept_privacy_prefix')} <a href={cfg.urls?.privacy} target="_blank" rel="noreferrer">{t('shop.checkout.accept_privacy_link')}</a> *</span>
                        </label>
                        <label className="gas-checkbox-custom">
                            <input type="checkbox" checked={subscribeNewsletter} onChange={(e) => setSubscribeNewsletter(e.target.checked)} />
                            <span>{t('shop.checkout.subscribe_newsletter')}</span>
                        </label>
                    </div>

                    <button type="submit" className="gas-shop-checkout-submit-button" disabled={submitting || !canSubmit}>
                        {submitting ? t('shop.checkout.processing') : submitButtonText}
                    </button>

                    <a href={cfg.urls?.cart} className="gas-checkout-back-link">← {t('shop.checkout.back_to_cart')}</a>
                </div>
            </form>
        </div>
    );
}
